import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MoreVertical, Trash2, Edit2, Pin, PinOff } from 'lucide-react';
import { useAlbum, useUpdateAlbum, useDeleteAlbum } from '@/hooks/use-albums';
import { useAlbumItems, useRemoveFromAlbum } from '@/hooks/use-albums';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';

export function AlbumView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  const { data: album, isLoading: loadingAlbum } = useAlbum(id);
  const { data: items = [], isLoading: loadingItems } = useAlbumItems(id);
  const removeFromAlbum = useRemoveFromAlbum();
  const updateAlbum = useUpdateAlbum();
  const deleteAlbum = useDeleteAlbum();

  useState(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id || null);
    });
  });

  const isOwner = currentUserId && album?.user_id === currentUserId;

  const handleTogglePin = async () => {
    if (!album || !id) return;
    await updateAlbum.mutateAsync({
      id,
      is_pinned: !album.is_pinned,
    });
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!id) return;
    await removeFromAlbum.mutateAsync({ itemId, albumId: id });
  };

  const handleDeleteAlbum = async () => {
    if (!id) return;
    await deleteAlbum.mutateAsync(id);
    navigate(-1);
  };

  if (loadingAlbum || loadingItems) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Album not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur-xl bg-black/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            {isOwner && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleTogglePin}>
                    {album.is_pinned ? (
                      <>
                        <PinOff className="w-4 h-4 mr-2" />
                        Unpin from profile
                      </>
                    ) : (
                      <>
                        <Pin className="w-4 h-4 mr-2" />
                        Pin to profile
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-red-600" onClick={handleDeleteAlbum}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Banner / First Image */}
      {items[0]?.image_url && (
        <div className="relative h-[50vh] overflow-hidden">
          <img
            src={items[0].image_url}
            alt="Cover"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          
          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-5xl">📁</span>
                {album.is_pinned && (
                  <Pin className="w-5 h-5 text-white/60" />
                )}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">{album.name}</h1>
              <p className="text-white/60 text-lg">
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pinterest-style Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {items.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="break-inside-avoid group relative"
            >
              <div className="relative rounded-xl overflow-hidden bg-white/5">
                <img
                  src={item.image_url || ''}
                  alt={item.caption || ''}
                  className="w-full h-auto"
                  loading="lazy"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    {item.caption && (
                      <p className="text-sm line-clamp-2">{item.caption}</p>
                    )}
                  </div>

                  {isOwner && (
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="absolute top-3 right-3 p-2 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {items.length === 0 && (
          <div className="text-center py-20">
            <span className="text-6xl mb-4 block">📁</span>
            <h2 className="text-2xl font-semibold mb-2">Empty album</h2>
            <p className="text-white/60">Add your content to see it here</p>
          </div>
        )}
      </div>
    </div>
  );
}
